import { Component } from 'react'
import { Box, Typography, Button, Paper, Stack } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { isChunkLoadFailure, triggerRecoveryReload } from '../lib/chunkLoadRecovery'
import { captureException } from '../lib/sentry.js'

/**
 * Error Boundary component to catch and display React errors gracefully
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    if (isChunkLoadFailure(error)) {
      triggerRecoveryReload(error)
      return
    }
    captureException(error, { componentStack: errorInfo?.componentStack })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  handleReloadPage = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      const chunkStale = isChunkLoadFailure(this.state.error)
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          p={3}
        >
          <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {chunkStale ? 'A new version is available' : 'Something went wrong'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {chunkStale
                ? 'The app was updated while you had this page open. Reload to fetch the latest version.'
                : (this.state.error?.message || 'An unexpected error occurred')}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              {chunkStale ? (
                <Button variant="contained" onClick={this.handleReloadPage}>
                  Reload page
                </Button>
              ) : (
                <>
                  <Button variant="contained" onClick={this.handleReset}>
                    Try again
                  </Button>
                  <Button variant="outlined" onClick={this.handleReloadPage}>
                    Reload page
                  </Button>
                </>
              )}
            </Stack>
          </Paper>
        </Box>
      )
    }

    return this.props.children
  }
}
